import {
  Modal as MuiModal,
  Stack,
  Typography
} from "@mui/material";
import {
  alpha,
  styled,
  useTheme
} from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ButtonText,
  Icon
} from "..";
import {
  useAuthStore
} from "../../stores";
import { getEnvironmentVariables } from "../../utils/general";

const StyledModal = styled(MuiModal)(({ theme }) => {
  const modalWidth = "30rem";
  return {
    backgroundColor: theme.customColors.backgroundPrimary,
    border: `2px solid ${theme.customColors.borderPrimaryControl}`,
    borderRadius: theme.spacing(2),
    boxShadow: "24px",
    height: "fit-content",
    left: "50%",
    maxHeight: "90vh",
    maxWidth: "calc(100vw - 32px)",
    overflow: "auto",
    padding: "24px 40px",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: modalWidth,

    ".MuiModal-backdrop":{
      backdropFilter:"blur(20px)",
      background: alpha(theme.customColors.backgroundPrimary, 0.7)
    }
  };});

export function ImpersonationModal({
  close,
  open=false
} : {
  close: () => void
  open: boolean
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const stopImpersonation = useAuthStore(state => state.stopImpersonation);
  const { adminLink } = getEnvironmentVariables();
  const sessionToken = useAuthStore(state => state.sessionToken);
  const authenticationMode = useAuthStore(state => state.mode);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const [disableButtons, setDisableButtons] = useState(false);
  const inititiveUrl =  smartConfiguration?.urlDomain
    ? `${smartConfiguration?.urlDomain}.${smartConfiguration.urlCorporateDomain}`
    : smartConfiguration?.urlCorporateDomain
      ?  `${smartConfiguration?.urlCorporateDomain}`
      : window.location.host;

  function deleteCookie(cname: string) {
    document.cookie =
    cname + "=delete;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/";
  }

  return (
    <StyledModal
      aria-labelledby="modal"
      open={ open }
      onClose={ close }
    >
      <Stack
        gap={ 2 }
        sx={ { "&:focus-visible": {
          outline: "none"
        } } }
      >
        <Stack alignItems="flex-end">
          <Icon
            icon={ "close" }
            size={ "16px" }
            cursor="pointer"
            color={ theme.customColors.textPrimary }
            transform="rotate(-90deg)"
            onClick={ close }
          />
        </Stack>
        <Stack
          alignItems="center"
          gap={ 4 }
        >
          <Stack
            alignItems="center"
            gap={ 1 }
          >
            <Typography
              aria-labelledby="modal-modal-title"
              color={ theme.customColors.textPrimary }
              fontSize="1.8rem"
              fontWeight={ 900 }
              textAlign="center"
            >
              { t("end-impersonation-title") }
            </Typography>
            <Typography
              aria-describedby="modal-modal-description"
              color={ theme.customColors.exploreCardText }
              fontSize="1rem"
              textAlign="center"
              sx={ { whiteSpace: "pre-line" } }
            >
              { t("end-impersonation-description") }
            </Typography>
          </Stack>
          <Stack
            direction="row"
            gap={ 2 }
          >
            <ButtonText
              variant="outlined"
              onClick={ close }
              disabled={ disableButtons }
              sx={ {
                "&.Mui-disabled": {
                  color: theme.customColors.textPrimary,
                  opacity: 0.5
                },
                ":hover": { backgroundColor: alpha(theme.customColors.backgroundPrimaryCta, 0.9) }
              } }
            >
              { t("annul") }
            </ButtonText>
            <ButtonText
              onClick={ () => {
                setDisableButtons(true);
                stopImpersonation();
                setTimeout(() => {
                  deleteCookie("activeLangCode");
                  deleteCookie("isImpersonation");
                  localStorage.removeItem("authState");
                  // eslint-disable-next-line max-len
                  window.location.href = `${adminLink}?sessionId=${sessionToken}&mode=${authenticationMode}&initiativeUrl=${inititiveUrl}`;
                }, 3000);
              } }
              disabled={ disableButtons }
              sx={ {
                "&.Mui-disabled": {
                  color: theme.customColors.textPrimaryCta,
                  opacity: 0.5
                },
                ":hover": { backgroundColor: alpha(theme.customColors.backgroundPrimaryCta, 0.9) }
              } }
            >
              { t("confirm") }
            </ButtonText>
          </Stack>
        </Stack>
      </Stack>
    </StyledModal>
  );
}
